import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import GabineteChip from "@/components/GabineteChip";
import ExpedienteCard from "@/components/inbox/ExpedienteCard";
import BandejaStatCards from "@/components/inbox/BandejaStatCards";
import {
  MagnifyingGlass, ArrowsClockwise, ArrowRight, X, FunnelSimple,
  Calendar, DownloadSimple, FileXls, FilePdf,
} from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";

const FASE_OPTIONS = [
  { k: "", label: "Todos los pendientes" },
  { k: "pendientes_contacto", label: "Pendientes de contacto" },
  { k: "pendientes_visita", label: "Pendientes de visita" },
  { k: "pendientes_asignacion", label: "Pendientes de asignación" },
  { k: "pendientes_finalizacion", label: "Pendientes de finalización" },
  { k: "pendientes_previo", label: "Pendientes de previo" },
  { k: "pendientes_cierre", label: "Pendientes de cierre" },
  { k: "asignados_hoy", label: "Asignados hoy" },
  { k: "incidencias", label: "Con incidencia" },
  { k: "ampliaciones", label: "Ampliaciones" },
  { k: "congelados", label: "Congelados · Standby" },
  { k: "consultas_mapfre", label: "Consultas Mapfre · <24h" },
  { k: "subencargo_alto", label: "Subencargos > 1.000 €" },
];

const FASE_LABELS = Object.fromEntries(FASE_OPTIONS.map((o) => [o.k, o.label]));

/**
 * Chip compacto para presets rápidos de fecha (Hoy, Semana, Mes, etc.).
 * Resaltado en azul cuando el rango actual coincide con el preset.
 */
function DatePresetChip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-medium px-2 py-1 rounded transition-colors whitespace-nowrap ${
        active
          ? "bg-blue-600 text-white"
          : "text-zinc-600 hover:bg-zinc-100"
      }`}
    >
      {children}
    </button>
  );
}

// =====================================================================
// Helpers para antigüedad y plazos de la aseguradora
// =====================================================================

// Parsea valores como "24h", "1 día", "5 días", "30 días" → horas (number)
function parsePlazoHoras(s) {
  if (!s || typeof s !== "string") return null;
  const v = s.trim().toLowerCase();
  // Captura número + unidad
  const m = v.match(/(\d+(?:[.,]\d+)?)\s*(h|hora|horas|d|día|días|dias)/);
  if (!m) {
    const onlyNum = v.match(/^(\d+)$/);
    if (onlyNum) return parseInt(onlyNum[1], 10) * 24; // sin unidad → días
    return null;
  }
  const num = parseFloat(m[1].replace(",", "."));
  const unit = m[2];
  if (unit.startsWith("h")) return num;
  return num * 24;
}

// Horas desde una fecha ISO hasta ahora
function horasDesde(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 3600000;
}

function diasDesde(iso) {
  const h = horasDesde(iso);
  if (h === null) return null;
  return Math.max(0, Math.floor(h / 24));
}

/**
 * Calcula el color de antigüedad basado en el siguiente hito pendiente y
 * los plazos definidos por la aseguradora. Si la aseguradora no tiene
 * plazos configurados, se usa el fallback 3/7 días.
 *
 * Devuelve { color: 'green' | 'amber' | 'red', label, hint }.
 */
function getAntiguedadStatus(exp, aseguradora) {
  // Los siniestros en standby ("congelado") no cuentan días: están
  // pausados a la espera de documentación o de la propia compañía. Se
  // devuelven con color="frozen" y dias=null para que la bandeja muestre
  // un badge específico sin presión temporal.
  if ((exp?.estado_encargo || "") === "congelado") {
    return { color: "frozen", dias: null, hint: "Congelado · no cuenta días" };
  }

  const plazos = aseguradora?.plazos || {};
  const plazoContacto = parsePlazoHoras(plazos.contacto_asegurado);
  const plazoPrelim = parsePlazoHoras(plazos.informe_preliminar);
  const plazoFinal = parsePlazoHoras(plazos.informe_final);

  const horasDesdeApertura = horasDesde(exp.created_at);
  const dias = horasDesdeApertura == null ? null : Math.floor(horasDesdeApertura / 24);

  // Sin plazos configurados → fallback 3/7 días
  if (!plazoContacto && !plazoPrelim && !plazoFinal) {
    let color = "green";
    if (dias === null) color = "green";
    else if (dias > 7) color = "red";
    else if (dias >= 3) color = "amber";
    return { color, dias, hint: "Plazos genéricos (sin configuración)" };
  }

  // Determinar siguiente hito pendiente y comparar con su plazo
  let plazoH = null;
  let etapa = "";
  if (!exp.fecha_contacto && plazoContacto) {
    plazoH = plazoContacto;
    etapa = "contacto";
  } else if (!exp.fecha_preliminar && plazoPrelim) {
    plazoH = plazoPrelim;
    etapa = "preliminar";
  } else if (!exp.fecha_cierre && plazoFinal) {
    plazoH = plazoFinal;
    etapa = "final";
  } else {
    // Todo dentro de plazo / completado
    return { color: "green", dias, hint: "Dentro de plazo" };
  }

  const consumido = horasDesdeApertura / plazoH;
  let color = "green";
  if (consumido > 1) color = "red";
  else if (consumido >= 0.7) color = "amber";

  const restante = plazoH - horasDesdeApertura;
  const restanteLabel =
    restante > 24
      ? `${Math.floor(restante / 24)}d para ${etapa}`
      : restante > 0
      ? `${Math.floor(restante)}h para ${etapa}`
      : `Vencido ${etapa}`;

  return { color, dias, hint: restanteLabel };
}

function AntiguedadBadge({ status, testid }) {
  // Standby / congelado: badge azul con icono ❄ y sin días (no cuentan).
  if (status?.color === "frozen") {
    return (
      <span
        data-testid={testid}
        title={status.hint || "Congelado · no cuenta días"}
        className="inline-flex items-center justify-center gap-1 rounded-md border text-[10.5px] font-bold px-1.5 py-0.5 bg-status-info-bg text-status-info-fg border-status-info-bd"
      >
        <span aria-hidden>❄</span>
        Standby
      </span>
    );
  }
  if (!status || status.dias === null || status.dias === undefined) {
    return <span className="text-muted text-[11px]">—</span>;
  }
  const colorCls = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    red: "bg-red-100 text-red-800 border-red-200",
  }[status.color] || "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <span
      data-testid={testid}
      title={status.hint || ""}
      className={`inline-flex items-center justify-center rounded-md border text-[11px] font-bold font-mono px-1.5 py-0.5 ${colorCls}`}
    >
      {status.dias}d
    </span>
  );
}

/**
 * Botón "Contactar" / "Contactado" con marcado en un click.
 * Pulsado registra la fecha de contacto = ahora.
 */
function ContactoButton({ exp, onMarked, testid }) {
  const [busy, setBusy] = useState(false);
  const contactado = !!exp.fecha_contacto;

  const mark = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (contactado || busy) return;
    setBusy(true);
    try {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      await api.put(`/expedientes/${exp.id}`, { fecha_contacto: `${yyyy}-${mm}-${dd}` });
      onMarked?.();
    } catch {
      alert("No se pudo marcar como contactado");
    } finally {
      setBusy(false);
    }
  };

  if (contactado) {
    const f = exp.fecha_contacto.length >= 10
      ? exp.fecha_contacto.slice(0, 10).split("-").reverse().join("/")
      : exp.fecha_contacto;
    return (
      <span
        data-testid={testid}
        title={`Contactado el ${f}`}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Contactado · {f}
      </span>
    );
  }
  return (
    <button
      onClick={mark}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:border-red-300 text-[10px] font-semibold px-1.5 py-0.5 transition-colors disabled:opacity-50"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      {busy ? "..." : "Contactar"}
    </button>
  );
}

/**
 * Botón de Visita con tres estados (pendiente / programada / visitada).
 * Al pulsar abre un mini-modal para elegir fecha + hora de programación
 * o marcar como visitada hoy.
 */
function VisitaButton({ exp, onMarked, testid }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const fechaIso = exp.fecha_visita;

  // Estado: pendiente | programada | visitada.
  // CRÍTICO: `visitada` requiere flag explícito `visita_realizada`. Antes se
  // calculaba sólo por comparación de fecha (fecha pasada → visitada), lo que
  // provocaba que las visitas programadas se marcaran como realizadas al
  // llegar la fecha aunque el perito no hubiera ido. Ahora una visita es
  // "visitada" ÚNICAMENTE cuando el perito la marca explícitamente.
  let estado = "pendiente";
  if (fechaIso) {
    estado = exp.visita_realizada === true ? "visitada" : "programada";
  }

  const fechaCorta = fechaIso && fechaIso.length >= 10
    ? fechaIso.slice(0, 10).split("-").reverse().join("/")
    : fechaIso;
  const hora = exp.hora_visita || "";

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (estado === "visitada") return;
    setOpen(true);
  };

  const guardarPrograma = async (fecha, horaStr) => {
    setBusy(true);
    try {
      const payload = { fecha_visita: fecha };
      if (horaStr) payload.hora_visita = horaStr;
      await api.put(`/expedientes/${exp.id}`, payload);
      setOpen(false);
      onMarked?.();
    } catch {
      alert("No se pudo guardar la visita");
    } finally {
      setBusy(false);
    }
  };

  const marcarHoy = async () => {
    setBusy(true);
    try {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const hh = String(now.getHours()).padStart(2, "0");
      const mi = String(now.getMinutes()).padStart(2, "0");
      // "Marcar hoy" → la visita se considera realizada AHORA. Persistimos
      // fecha + hora + visita_realizada=true como una sola operación.
      await api.put(`/expedientes/${exp.id}`, {
        fecha_visita: `${yyyy}-${mm}-${dd}`,
        hora_visita: `${hh}:${mi}`,
        visita_realizada: true,
      });
      setOpen(false);
      onMarked?.();
    } catch {
      alert("No se pudo guardar la visita");
    } finally {
      setBusy(false);
    }
  };

  let btn;
  if (estado === "visitada") {
    btn = (
      <span
        data-testid={testid}
        title={`Visitado el ${fechaCorta}`}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Visitado · {fechaCorta}
      </span>
    );
  } else if (estado === "programada") {
    btn = (
      <button
        onClick={handleClick}
        data-testid={testid}
        title="Click para reprogramar o marcar como realizada"
        className="inline-flex items-center gap-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 text-[10px] font-semibold px-1.5 py-0.5 transition-colors"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Programada · {fechaCorta}{hora ? " " + hora : ""}
      </button>
    );
  } else {
    btn = (
      <button
        onClick={handleClick}
        data-testid={testid}
        className="inline-flex items-center gap-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:border-red-300 text-[10px] font-semibold px-1.5 py-0.5 transition-colors"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Pendiente visita
      </button>
    );
  }

  return (
    <>
      {btn}
      {open && (
        <ProgramarVisitaModal
          initialDate={fechaIso?.slice(0, 10) || ""}
          initialTime={hora || ""}
          asegurado={exp.extracted_data?.asegurado_nombre || ""}
          onClose={() => setOpen(false)}
          onPrograma={guardarPrograma}
          onMarcarHoy={marcarHoy}
          busy={busy}
        />
      )}
    </>
  );
}

function ProgramarVisitaModal({ initialDate, initialTime, asegurado,
                                  onClose, onPrograma, onMarcarHoy, busy }) {
  // fecha por defecto: la guardada o mañana
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [fecha, setFecha] = useState(initialDate || fmt(tomorrow));
  const [hora, setHora] = useState(initialTime || "10:00");

  const stop = (e) => e.stopPropagation();

  return (
    <div
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-in fade-in"
      data-testid="programar-visita-overlay"
    >
      <div
        onClick={stop}
        className="bg-white rounded-lg shadow-xl border border-border w-full max-w-sm overflow-hidden"
        data-testid="programar-visita-modal"
      >
        <div className="px-4 py-3 border-b border-border bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Programar visita</div>
          <div className="font-display font-bold text-sm tracking-tight truncate">
            {asegurado || "Sin asegurado"}
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[12px] space-y-1">
              <span className="font-semibold">Fecha</span>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                onClick={stop}
                data-testid="programar-fecha"
                className="w-full"
              />
            </label>
            <label className="text-[12px] space-y-1">
              <span className="font-semibold">Hora</span>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                onClick={stop}
                data-testid="programar-hora"
                className="w-full"
              />
            </label>
          </div>
          <p className="text-[11px] text-muted leading-relaxed">
            Si la fecha es futura, se marca como <b>Programada</b> (amarillo).
            Cuando llegue el día, ese mismo botón pasará a poder marcarse como visitada.
          </p>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t border-border flex items-center gap-2 flex-wrap">
          <button
            onClick={(e) => { stop(e); onPrograma(fecha, hora); }}
            disabled={busy || !fecha}
            data-testid="programar-save"
            className="text-[12px] bg-foreground text-white font-semibold rounded-md px-3 py-1.5 hover:bg-foreground/90 disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Programar"}
          </button>
          <button
            onClick={(e) => { stop(e); onMarcarHoy(); }}
            disabled={busy}
            data-testid="programar-hoy"
            className="text-[12px] bg-emerald-600 text-white font-semibold rounded-md px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50"
          >
            Marcar visitada hoy
          </button>
          <button
            onClick={(e) => { stop(e); onClose(); }}
            disabled={busy}
            className="ml-auto text-[12px] text-muted hover:text-foreground"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

const DATE_FIELDS = [
  { k: "apertura", label: "Apertura" },
  { k: "asignacion", label: "Asignación" },
  { k: "contacto", label: "Contacto" },
  { k: "visita", label: "Visita" },
  { k: "cierre", label: "Finalización" },
];

export default function Inbox() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const fase = searchParams.get("fase") || "";
  const provincia = searchParams.get("provincia") || "";
  const aseguradoraId = searchParams.get("aseguradora_id") || "";
  const tipoEncargoFilter = searchParams.get("tipo_encargo") || "";
  const [items, setItems] = useState([]);
  const [landingUnread, setLandingUnread] = useState({}); // { expediente_id: nº mensajes no leídos del asegurado }
  const [provincias, setProvincias] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [gabinetes, setGabinetes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  // Roles que NO ven el botón de exportar (solo perito_externo).
  const canExport = user?.role !== "perito_externo";

  // Default por rol al entrar SIN parámetros: los gestores y admins
  // ven los pendientes de contacto (es el cuello de botella inicial).
  // Los peritos ven sus expedientes (el backend ya filtra por perito,
  // no aplicamos fase para no ocultarles los suyos).
  useEffect(() => {
    if (searchParams.toString()) return; // ya hay filtros en URL
    const role = user?.role;
    if (!role) return;
    if (role === "admin" || role === "gestor_senior" || role === "gestor_siniestros") {
      const next = new URLSearchParams();
      next.set("fase", "pendientes_contacto");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Cierra el menú al hacer click fuera
  useEffect(() => {
    const onClick = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // Lectura reactiva del rango de fechas activo desde la URL.
  // Sólo hay UN rango activo cada vez: el campo (`apertura`/`asignacion`/...)
  // determina sobre qué fecha se aplica `desde` / `hasta`.
  const dateField = searchParams.get("date_field") || "apertura";
  const dateDesde = searchParams.get("date_desde") || "";
  const dateHasta = searchParams.get("date_hasta") || "";
  const dateActive = !!(dateDesde || dateHasta);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fase) params.set("fase", fase);
      if (provincia) params.set("provincia", provincia);
      if (aseguradoraId) params.set("aseguradora_id", aseguradoraId);
      if (tipoEncargoFilter) params.set("tipo_encargo", tipoEncargoFilter);
      if (dateActive) {
        if (dateDesde) params.set(`${dateField}_desde`, dateDesde);
        if (dateHasta) params.set(`${dateField}_hasta`, dateHasta);
      }
      params.set("limit", "500");
      const r = await api.get(`/expedientes?${params.toString()}`);
      setItems(r.data);
      // Carga de contadores de actividad del asegurado para badges.
      // No bloqueamos el render principal: fire-and-forget.
      const ids = (r.data || []).map((e) => e.id).filter(Boolean);
      if (ids.length) {
        api.get(`/expedientes/landing-activity/unread-counts?ids=${ids.join(",")}`)
          .then((rr) => setLandingUnread(rr.data || {}))
          .catch(() => setLandingUnread({}));
      } else {
        setLandingUnread({});
      }
    } finally {
      setLoading(false);
    }
  };

  // Provincias disponibles para el filtro (lista única con conteo)
  useEffect(() => {
    let cancelled = false;
    api.get("/expedientes/_facets/provincias")
      .then((r) => { if (!cancelled) setProvincias(r.data); })
      .catch(() => { if (!cancelled) setProvincias([]); });
    return () => { cancelled = true; };
  }, []);

  // Aseguradoras (para resolver plazos por nombre)
  useEffect(() => {
    let cancelled = false;
    api.get("/aseguradoras")
      .then((r) => { if (!cancelled) setAseguradoras(r.data || []); })
      .catch(() => { if (!cancelled) setAseguradoras([]); });
    return () => { cancelled = true; };
  }, []);

  // Gabinetes (para pintar el chip de etiqueta corta junto a la aseguradora).
  // Es una lista corta — se cachea una vez al montar.
  useEffect(() => {
    let cancelled = false;
    api.get("/gabinetes")
      .then((r) => { if (!cancelled) setGabinetes(r.data || []); })
      .catch(() => { if (!cancelled) setGabinetes([]); });
    return () => { cancelled = true; };
  }, []);

  const gabineteById = useMemo(() => {
    const m = new Map();
    for (const g of gabinetes) m.set(g.id, g);
    return m;
  }, [gabinetes]);

  // Diccionario nombre-aseguradora → objeto, para resolver plazos rápido
  const asegByName = useMemo(() => {
    const map = {};
    for (const a of aseguradoras) {
      if (a.name) map[a.name.toLowerCase()] = a;
      if (a.nombre_compacto) map[a.nombre_compacto.toLowerCase()] = a;
      if (a.code) map[a.code.toLowerCase()] = a;
    }
    return map;
  }, [aseguradoras]);

  const resolveAseguradora = (exp) => {
    const ed = exp.extracted_data || {};
    const candidates = [exp.detected_aseguradora, ed.aseguradora].filter(Boolean);
    for (const c of candidates) {
      const a = asegByName[c.toLowerCase()];
      if (a) return a;
    }
    return null;
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [fase, provincia, aseguradoraId, tipoEncargoFilter, dateField, dateDesde, dateHasta]);

  // Construye los query params actuales para reaprovecharlos en exportación.
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (fase) params.set("fase", fase);
    if (provincia) params.set("provincia", provincia);
    if (dateActive) {
      if (dateDesde) params.set(`${dateField}_desde`, dateDesde);
      if (dateHasta) params.set(`${dateField}_hasta`, dateHasta);
    }
    return params;
  };

  const doExport = async (format) => {
    setExportOpen(false);
    setExporting(true);
    try {
      const params = buildQueryParams();
      params.set("format", format);
      const token = getToken();
      const r = await fetch(`${API}/expedientes/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("No se pudo exportar");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      // El backend devuelve formato `subencargo_xlsx` para la relación; usamos
      // `xlsx` en el nombre final del archivo.
      const ext = format === "subencargo_xlsx" ? "xlsx" : format;
      const stem = format === "subencargo_xlsx" ? "subencargos-altos" : `bandeja-${fase || "todos"}`;
      a.download = `${stem}-${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert("Error al exportar la bandeja");
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => items.filter((e) => {
    if (!q) return true;
    const qq = q.toLowerCase();
    return (
      e.source_filename?.toLowerCase().includes(qq) ||
      e.extracted_data?.numero_siniestro?.toLowerCase().includes(qq) ||
      e.extracted_data?.asegurado_nombre?.toLowerCase().includes(qq) ||
      e.extracted_data?.aseguradora?.toLowerCase().includes(qq) ||
      e.extracted_data?.direccion_riesgo?.toLowerCase().includes(qq) ||
      e.extracted_data?.codigo_postal?.toLowerCase().includes(qq)
    );
  }), [items, q]);

  const hasFiltrosWorkflow = !!(fase || provincia || aseguradoraId || tipoEncargoFilter || dateActive);

  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const clearDates = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("date_field");
    next.delete("date_desde");
    next.delete("date_hasta");
    setSearchParams(next, { replace: true });
  };

  const dateLabel = DATE_FIELDS.find((f) => f.k === dateField)?.label || "Apertura";

  // ===== Presets rápidos de fecha (Hoy / Esta semana / Este mes / Mes pasado / Elegir mes) =====
  // Calculan `desde` y `hasta` (YYYY-MM-DD en zona local) y lo aplican junto.
  const ymdLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const applyDateRange = (desde, hasta) => {
    const next = new URLSearchParams(searchParams);
    next.set("date_desde", desde);
    next.set("date_hasta", hasta);
    if (!next.get("date_field")) next.set("date_field", "apertura");
    setSearchParams(next, { replace: true });
  };

  const presetToday = () => {
    const t = ymdLocal(new Date());
    applyDateRange(t, t);
  };
  const presetThisWeek = () => {
    const now = new Date();
    // Lunes = inicio de semana (es-ES). getDay(): 0 dom, 1 lun, ..., 6 sáb.
    const dow = now.getDay();
    const diffToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    applyDateRange(ymdLocal(monday), ymdLocal(now));
  };
  const presetThisMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    applyDateRange(ymdLocal(first), ymdLocal(last));
  };
  const presetLastMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    applyDateRange(ymdLocal(first), ymdLocal(last));
  };
  const onChooseMonth = (ev) => {
    // input type="month" → "YYYY-MM"
    const v = ev.target.value;
    if (!v) return;
    const [yStr, mStr] = v.split("-");
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10) - 1;
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    applyDateRange(ymdLocal(first), ymdLocal(last));
  };

  // Detección del preset activo para resaltar el chip (basado en desde/hasta
  // exactos, sin tocar el dateField — el usuario puede combinar preset+campo).
  const activePreset = useMemo(() => {
    if (!dateDesde || !dateHasta) return null;
    const today = ymdLocal(new Date());
    if (dateDesde === today && dateHasta === today) return "today";
    const now = new Date();
    const dow = now.getDay();
    const diffToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(now); monday.setDate(now.getDate() - diffToMonday);
    if (dateDesde === ymdLocal(monday) && dateHasta === today) return "week";
    const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastThis = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    if (dateDesde === ymdLocal(firstThis) && dateHasta === ymdLocal(lastThis)) return "month";
    const firstLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastLast = new Date(now.getFullYear(), now.getMonth(), 0);
    if (dateDesde === ymdLocal(firstLast) && dateHasta === ymdLocal(lastLast)) return "last_month";
    return null;
  }, [dateDesde, dateHasta]);

  const chosenMonthValue = useMemo(() => {
    // Si el rango cubre un mes natural completo, dejamos el input "Elegir mes"
    // sincronizado a ese mes (para feedback visual).
    if (!dateDesde || !dateHasta) return "";
    const d1 = new Date(dateDesde);
    const d2 = new Date(dateHasta);
    if (isNaN(d1) || isNaN(d2)) return "";
    if (d1.getDate() !== 1) return "";
    const lastOfMonth = new Date(d1.getFullYear(), d1.getMonth() + 1, 0).getDate();
    if (d2.getDate() !== lastOfMonth) return "";
    if (d1.getMonth() !== d2.getMonth() || d1.getFullYear() !== d2.getFullYear()) return "";
    return `${d1.getFullYear()}-${String(d1.getMonth() + 1).padStart(2, "0")}`;
  }, [dateDesde, dateHasta]);

  return (
    <div data-testid="inbox-page">
      <header className="h-14 border-b border-border bg-white px-3 md:px-6 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Bandeja</div>
            <h1 className="font-display font-bold text-sm md:text-lg tracking-tight truncate">{filtered.length} expedientes</h1>
          </div>
          {fase && (
            <span
              className="hidden md:inline-flex items-center gap-1.5 bg-brand-soft text-brand-fg border border-brand/30 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              data-testid="fase-chip"
            >
              {FASE_LABELS[fase] || fase}
              <button
                onClick={() => setParam("fase", null)}
                className="hover:text-foreground"
                data-testid="clear-fase"
              >
                <X className="w-3 h-3" weight="bold" />
              </button>
            </span>
          )}
          {provincia && (
            <span
              className="hidden md:inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 border border-violet-200 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              data-testid="provincia-chip"
            >
              {provincia}
              <button
                onClick={() => setParam("provincia", null)}
                className="hover:text-foreground"
                data-testid="clear-provincia"
              >
                <X className="w-3 h-3" weight="bold" />
              </button>
            </span>
          )}
          {aseguradoraId && (() => {
            const a = aseguradoras.find((x) => x.id === aseguradoraId);
            return (
              <span
                className="hidden md:inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                data-testid="aseguradora-chip"
              >
                {a?.name || aseguradoraId}
                <button
                  onClick={() => setParam("aseguradora_id", null)}
                  className="hover:text-foreground"
                  data-testid="clear-aseguradora"
                >
                  <X className="w-3 h-3" weight="bold" />
                </button>
              </span>
            );
          })()}
          {tipoEncargoFilter && (
            <span
              className="hidden md:inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              data-testid="tipo-encargo-chip"
            >
              Tipo: {tipoEncargoFilter}
              <button
                onClick={() => setParam("tipo_encargo", null)}
                className="hover:text-foreground"
                data-testid="clear-tipo-encargo"
              >
                <X className="w-3 h-3" weight="bold" />
              </button>
            </span>
          )}
          {dateActive && (
            <span
              className="hidden md:inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              data-testid="date-chip"
            >
              <Calendar className="w-3 h-3" weight="bold" />
              {dateLabel}: {dateDesde || "…"} → {dateHasta || "…"}
              <button onClick={clearDates} className="hover:text-foreground" data-testid="clear-date">
                <X className="w-3 h-3" weight="bold" />
              </button>
            </span>
          )}
          {hasFiltrosWorkflow && (
            <button
              onClick={clearAllFilters}
              className="text-[11px] text-muted hover:text-foreground underline-offset-2 hover:underline"
              data-testid="clear-all-filters"
            >
              Limpiar
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <div ref={exportRef} className="relative">
              <button
                onClick={() => setExportOpen((v) => !v)}
                disabled={exporting}
                data-testid="inbox-export"
                className="text-xs px-2.5 py-1.5 border border-border rounded-md hover:bg-surface inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <DownloadSimple className="w-3.5 h-3.5" weight="bold" />
                <span className="hidden sm:inline">{exporting ? "Exportando..." : "Exportar"}</span>
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-border rounded-md shadow-lg w-56 overflow-hidden">
                  <button
                    onClick={() => doExport("xlsx")}
                    data-testid="inbox-export-xlsx"
                    className="w-full text-left text-[12px] px-3 py-2 hover:bg-surface inline-flex items-center gap-2"
                  >
                    <FileXls className="w-4 h-4 text-emerald-700" weight="duotone" />
                    Excel (.xlsx)
                  </button>
                  <button
                    onClick={() => doExport("pdf")}
                    data-testid="inbox-export-pdf"
                    className="w-full text-left text-[12px] px-3 py-2 hover:bg-surface inline-flex items-center gap-2 border-t border-border/60"
                  >
                    <FilePdf className="w-4 h-4 text-red-700" weight="duotone" />
                    PDF (A4 horizontal)
                  </button>
                  <button
                    onClick={() => doExport("subencargo_xlsx")}
                    data-testid="inbox-export-subencargo"
                    className="w-full text-left text-[12px] px-3 py-2 hover:bg-surface inline-flex items-start gap-2 border-t border-border/60"
                    title="Lista reducida para enviar a la compañía: Nº siniestro · Dirección · Importe tasación"
                  >
                    <FileXls className="w-4 h-4 text-amber-700 mt-0.5" weight="duotone" />
                    <span className="flex-1">
                      <div className="font-semibold">Relación subencargos &gt; 1.000 €</div>
                      <div className="text-[10px] text-muted leading-tight">Nº siniestro · Dirección · Importe</div>
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
          <button onClick={load} className="text-xs text-muted hover:text-foreground inline-flex items-center gap-1.5" data-testid="inbox-refresh">
            <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </header>

      <div className="p-3 md:p-6">
        {/* Tarjetas-acceso superiores: 6 atajos rápidos de filtrado por fase
            (contacto, visita, preliminar, cierre, consultas Mapfre, standby).
            La consulta Mapfre incluye sub-badge "X vencidas" si alguna supera el SLA de 24h. */}
        <BandejaStatCards
          activeFase={fase}
          onSelect={(k) => setParam("fase", k || null)}
        />

        {/* Filtros: workflow + provincia + aseguradora + fechas + búsqueda */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
            {/* Workflow */}
            <div className="inline-flex items-center gap-1.5 text-[11px] text-muted">
              <FunnelSimple className="w-3.5 h-3.5" weight="bold" />
              <span className="uppercase tracking-widest">Workflow</span>
            </div>
            <select
              value={fase}
              onChange={(e) => setParam("fase", e.target.value || null)}
              data-testid="workflow-filter"
              className="!w-auto !text-[13px] !py-1.5 !pl-3 !pr-8 min-w-[210px]"
            >
              {FASE_OPTIONS.map((o) => (
                <option key={o.k || "todos"} value={o.k}>{o.label}</option>
              ))}
            </select>

            {/* Provincia */}
            <select
              value={provincia}
              onChange={(e) => setParam("provincia", e.target.value || null)}
              data-testid="provincia-filter"
              className="!w-auto !text-[13px] !py-1.5 !pl-3 !pr-8 min-w-[180px]"
            >
              <option value="">Todas las provincias</option>
              {provincias.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} ({p.count})
                </option>
              ))}
            </select>

            {/* Aseguradora */}
            <select
              value={aseguradoraId}
              onChange={(e) => setParam("aseguradora_id", e.target.value || null)}
              data-testid="aseguradora-filter"
              className="!w-auto !text-[13px] !py-1.5 !pl-3 !pr-8 min-w-[180px]"
            >
              <option value="">Todas las aseguradoras</option>
              {aseguradoras
                .filter((a) => a.active !== false)
                .map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
            </select>

            {/* Presets rápidos de fecha */}
            <div
              className="inline-flex items-center gap-1 bg-white border border-border rounded-md px-1 py-1"
              data-testid="date-presets"
            >
              <DatePresetChip
                active={activePreset === "today"}
                onClick={presetToday}
                testid="date-preset-today"
              >Hoy</DatePresetChip>
              <DatePresetChip
                active={activePreset === "week"}
                onClick={presetThisWeek}
                testid="date-preset-week"
              >Semana</DatePresetChip>
              <DatePresetChip
                active={activePreset === "month"}
                onClick={presetThisMonth}
                testid="date-preset-month"
              >Este mes</DatePresetChip>
              <DatePresetChip
                active={activePreset === "last_month"}
                onClick={presetLastMonth}
                testid="date-preset-last-month"
              >Mes pasado</DatePresetChip>
              <input
                type="month"
                value={chosenMonthValue}
                onChange={onChooseMonth}
                data-testid="date-preset-pick-month"
                title="Elegir mes"
                className="!text-[12px] !py-0.5 !px-1.5 !border-0 !bg-transparent !shadow-none focus:!ring-0 !w-[120px]"
              />
            </div>

            {/* Fechas — cascada: tipo + desde + hasta en un único bloque */}
            <div
              className="inline-flex items-center gap-1 bg-white border border-border rounded-md px-1.5 py-1"
              data-testid="date-block"
            >
              <Calendar className="w-3.5 h-3.5 text-muted ml-1" weight="bold" />
              <select
                value={dateField}
                onChange={(e) => setParam("date_field", e.target.value)}
                data-testid="date-field"
                className="!text-[12px] !py-1 !pl-1.5 !pr-7 !border-0 !bg-transparent !shadow-none focus:!ring-0"
              >
                {DATE_FIELDS.map((d) => (
                  <option key={d.k} value={d.k}>{d.label}</option>
                ))}
              </select>
              <span className="text-[10px] text-muted">desde</span>
              <input
                type="date"
                value={dateDesde}
                onChange={(e) => setParam("date_desde", e.target.value || null)}
                data-testid="date-desde"
                className="!text-[12px] !py-1 !px-1.5 !border-0 !bg-transparent !shadow-none focus:!ring-0 !w-[120px]"
              />
              <span className="text-[10px] text-muted">hasta</span>
              <input
                type="date"
                value={dateHasta}
                onChange={(e) => setParam("date_hasta", e.target.value || null)}
                data-testid="date-hasta"
                className="!text-[12px] !py-1 !px-1.5 !border-0 !bg-transparent !shadow-none focus:!ring-0 !w-[120px]"
              />
              {dateActive && (
                <button
                  onClick={clearDates}
                  data-testid="clear-date-inline"
                  className="text-muted hover:text-foreground p-1"
                  title="Limpiar fechas"
                >
                  <X className="w-3 h-3" weight="bold" />
                </button>
              )}
            </div>

            {/* Búsqueda libre */}
            <div className="relative">
              <MagnifyingGlass className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted" weight="bold" />
              <input
                placeholder="Buscar nº siniestro, asegurado..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="!pl-8 text-[13px]"
                data-testid="inbox-search"
              />
            </div>
        </div>

        {/* Lista de fichas (rediseño v2): tabla y cards mobile sustituidas
            por un único listado de tarjetas ExpedienteCard responsivas.
            En lg+ muestra stepper de workflow; en móvil se compacta. */}
        <div
          className="space-y-2.5"
          data-testid="expedientes-card-list"
        >
          {loading && filtered.length === 0 && (
            <div className="py-10 text-center text-muted bg-white rounded-lg border border-border">Cargando...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-muted bg-white rounded-lg border border-border">Sin expedientes que coincidan.</div>
          )}
          {filtered.map((e) => {
            const asg = resolveAseguradora(e);
            const status = getAntiguedadStatus(e, asg);
            return (
              <ExpedienteCard
                key={e.id}
                exp={e}
                status={status}
                gabinete={gabineteById.get(e.gabinete_id)}
                aseguradora={asg}
                aseguradoUnread={landingUnread[e.id] || 0}
                testid={`expediente-card-${e.id}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
